'use client'

import { type MouseEvent } from 'react'

import { cn } from '@/shared/lib'

import { Thumbnail, type Props as ThumbnailProps } from '../thumbnail'
import { useThumbnail } from './lib/thumbnail'
import { useCarousel } from './lib/use-carousel'
import { IconButton } from '../button/icon-button'

type Props = Omit<Partial<ThumbnailProps>, 'onClick' | 'alt'> & {
  displayLimit?: number
  images: string[]
  alt: string | ((index: number) => string)
  onClick?: (index: number) => void
  className?: string
}

export const Thumbnails = ({
  displayLimit = 6,
  images,
  ratio = '4:3',
  size = 64,
  rounded = true,
  alt,
  onClick,
  className,
}: Props) => {
  const { selectedIndex, scrollTo } = useCarousel()

  const {
    containerWidth,
    currentPageOffset,
    pagination,
    goToPrevPage,
    goToNextPage,
  } = useThumbnail({
    ratio,
    size,
    displayLimit,
    totalItemCount: images.length,
  })

  const handleClick = (index: number) => () => {
    onClick?.(index)
  }

  const handleContextMenu = (event: MouseEvent<HTMLImageElement>) => {
    event.preventDefault()
  }

  const handleMouseEnter = (index: number) => () => {
    scrollTo(index)
  }

  const handlePrevButtonClick = () => {
    goToPrevPage()
  }

  const handleNextButtonClick = () => {
    goToNextPage()
  }

  return (
    <div className={cn('flex items-center gap-6.5', className)}>
      <div
        className="shrink overflow-hidden"
        style={{ width: `${containerWidth}px` }}
      >
        <ul
          className={cn(
            'flex transform-gpu gap-3 transition-transform duration-300 ease-in-out',
          )}
          style={{
            transform: `translateX(-${currentPageOffset}px)`,
          }}
        >
          {images.map((image, index) => {
            const isSelected = selectedIndex === index

            return (
              <li key={image} onMouseEnter={handleMouseEnter(index)}>
                <Thumbnail
                  src={image}
                  alt={typeof alt === 'function' ? alt(index) : alt}
                  ratio={ratio}
                  size={size}
                  rounded={rounded}
                  className={cn(
                    'cursor-pointer rounded-sm select-none',
                    !isSelected && 'opacity-60',
                  )}
                  onClick={handleClick(index)}
                  placeholder
                  onContextMenu={handleContextMenu}
                />
              </li>
            )
          })}
        </ul>
      </div>
      <div className="flex gap-3">
        <IconButton
          name="arrow-left"
          variant="outline"
          size="large"
          shape="pill"
          disabled={pagination.isFirstPage}
          onClick={handlePrevButtonClick}
          aria-label="이전 페이지로 이동"
        />
        <IconButton
          name="arrow-right"
          variant="outline"
          size="large"
          shape="pill"
          disabled={pagination.isLastPage}
          onClick={handleNextButtonClick}
          aria-label="다음 페이지로 이동"
        />
      </div>
    </div>
  )
}
